import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

export type DbPortalKind = 'crm' | 'it' | 'dem' | 'forms';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(url && anonKey);

const primaryAuthOptions = {
  persistSession: true,
  autoRefreshToken: true,
  detectSessionInUrl: true,
} as const;

/**
 * Secondary schema clients must NOT share persistSession/autoRefresh with CRM.
 * Multiple GoTrue clients on the same storage key deadlock on login (stuck on
 * "Verifying access…"), especially after soft portal switches / mobile Safari.
 */
function makeSecondarySchemaClient(schema: string): SupabaseClient {
  return createClient(url, anonKey, {
    db: { schema },
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: `bth-secondary-${schema}`,
    },
  }) as SupabaseClient;
}

export const supabaseCrm = isSupabaseConfigured
  ? (createClient(url, anonKey, {
      db: { schema: 'crm' },
      auth: primaryAuthOptions,
    }) as unknown as SupabaseClient)
  : (null as unknown as SupabaseClient);

export const supabaseIt = isSupabaseConfigured
  ? makeSecondarySchemaClient('it')
  : (null as unknown as SupabaseClient);

export const supabaseDem = isSupabaseConfigured
  ? makeSecondarySchemaClient('data_entry_management')
  : (null as unknown as SupabaseClient);

export const supabaseForms = isSupabaseConfigured
  ? makeSecondarySchemaClient('application_form')
  : (null as unknown as SupabaseClient);

/** Shared auth session (either client works for sign-in). */
export const supabase = supabaseCrm;

let activePortalKind: DbPortalKind = 'crm';

export function setActivePortalKind(kind: DbPortalKind): void {
  activePortalKind = kind;
}

export function getActivePortalKind(): DbPortalKind {
  return activePortalKind;
}

/** Push the CRM JWT into it/dem/forms in-memory clients (SPA soft navigation). */
let propagatePromise: Promise<void> | null = null;

export function propagateAuthSession(session: Session | null): Promise<void> {
  if (!isSupabaseConfigured || !session) return Promise.resolve();
  const payload = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  };
  propagatePromise = Promise.allSettled([
    supabaseIt.auth.setSession(payload),
    supabaseDem.auth.setSession(payload),
    supabaseForms.auth.setSession(payload),
  ]).then(() => undefined);
  return propagatePromise;
}

/**
 * Keep schema-scoped clients on the same JWT as the primary auth client.
 * Never call setSession from inside onAuthStateChange (deadlocks GoTrueClient).
 */
export async function syncSecondaryAuthSessions(): Promise<void> {
  if (!isSupabaseConfigured) return;
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) return;
  await propagateAuthSession(data.session);
}

/** Wait for the latest JWT fan-out (or sync from storage) with a hard timeout. */
export async function ensureSecondaryAuthSynced(timeoutMs = 5_000): Promise<void> {
  const work = propagatePromise ?? syncSecondaryAuthSessions();
  await Promise.race([
    work,
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, timeoutMs);
    }),
  ]);
}

export function resetSecondaryAuthSync(): void {
  propagatePromise = null;
}

if (isSupabaseConfigured) {
  // Defer — calling setSession inside onAuthStateChange deadlocks GoTrueClient.
  supabase.auth.onAuthStateChange((_event, session) => {
    window.setTimeout(() => {
      void propagateAuthSession(session);
    }, 0);
  });
}

export function getDbSupabase(kind: DbPortalKind = activePortalKind): SupabaseClient {
  if (kind === 'it') return supabaseIt;
  if (kind === 'dem') return supabaseDem;
  if (kind === 'forms') return supabaseForms;
  return supabaseCrm;
}

export function getCrmDbSchema(kind: DbPortalKind = activePortalKind): string {
  if (kind === 'it') return 'it';
  if (kind === 'dem') return 'data_entry_management';
  if (kind === 'forms') return 'application_form';
  return 'crm';
}
